import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { NAV_GROUPS, ADMIN_ITEM } from "@/constants/nav";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Scale } from "lucide-react";

export default function Sidebar() {
  const { user } = useAuth();
  const groups = [...NAV_GROUPS];
  const [overdue, setOverdue] = useState(0);

  useEffect(() => {
    const load = () => api.get("/matters/my-overdue-count").then((r) => setOverdue(r.data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  return (
    <aside
      className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))]"
      data-testid="sidebar"
    >
      <div className="flex items-center gap-3 px-5 h-16 border-b border-white/10">
        <img src="/monge-logo-footer.svg" alt="Monge & Associates" className="h-6 w-auto" />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {groups.map((g) => (
          <div key={g.label}>
            <div className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">
              {g.label}
            </div>
            <div className="space-y-0.5">
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150 ${
                      isActive
                        ? "bg-brand-red-grad text-primary-foreground font-semibold shadow-sm"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <it.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                  <span>{it.label}</span>
                  {it.to === "/matter" && overdue > 0 && (
                    <span data-testid="nav-matter-overdue" title={`${overdue} overdue task(s)`}
                      className="ml-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-white text-primary text-[10px] font-bold leading-none shadow-sm">
                      {overdue > 9 ? "9+" : overdue}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        {user?.role === "admin" && (
          <div>
            <div className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">
              Admin
            </div>
            <NavLink
              to={ADMIN_ITEM.to}
              data-testid={ADMIN_ITEM.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150 ${
                  isActive ? "bg-brand-red-grad text-primary-foreground font-semibold shadow-sm" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <ADMIN_ITEM.icon className="h-4 w-4" />
              <span>{ADMIN_ITEM.label}</span>
            </NavLink>
          </div>
        )}
      </nav>

      <div className="px-4 py-3 border-t border-white/10 flex flex-col gap-1 text-[11px] text-white/45">
        <div className="flex items-center gap-2 text-white/60"><Scale className="h-3.5 w-3.5 text-primary" /> Because You Want to Win</div>
        <div>Injury &amp; Accident Attorneys · Est. 1993</div>
      </div>
    </aside>
  );
}
